import GenericTable from "@/common/components/ui/table/GenericTable";
import {
  TableFilters,
  TableFiltersState,
} from "@/common/components/ui/table/TableFilters";
import {
  changeStatusExternalServices,
  getExternalServices,
} from "@/common/services/external-services.api";
import {
  EXTERNAL_SERVICE_STATUS,
  IExternalServices,
} from "@/types/external-services.type";
import { useContext, useEffect, useState } from "react";
import ModalRegisterExternalServices from "../modals/RegisterExternalServices.modal";
import TableFooter from "@/common/components/ui/table/TableFooter";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import Loading from "@/common/components/ui/loading/Loading";
import CancelarExternalServicesModal from "../modals/CancelarExternalServices.modal";
import EditExternalServicesModal from "../modals/EditExternalServices.modal";
import { getCompanyId } from "@/common/services/company.api";
import { formatDateAndTime } from "@/common/helpers/date.helpers";
import { getCompanySigla } from "@/common/helpers/company.helpers";
import { AuthContext } from "@/common/components/auth/authContext/AuthContext";
import { useCompanyStore } from "@/common/states/store/Company";

const headers = {
  companyId: "Cidade",
  createdAt: "Data",
  supplierName: "Fornecedor",
  requestorName: "Solicitante",
  licensePlate: "Placa",
  status: "Status",
  totalValue: "Valor Total",
  description: "Descrição",
};

const EMPTY_ACTION = { eventName: "", data: undefined };

export default function TableExternalServices() {
  useContext(AuthContext);
  const [externalServices, setExternalServices] = useState<IExternalServices[]>(
    []
  );
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const { notifyError, notifySuccess } = useToastContext();
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-updatedAt",
  });
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [showCreate, setShowCreate] = useState(false);
  const companyId = getCompanyId();
  const companyStore = useCompanyStore();

  const formatStyle = (
    content: React.ReactNode,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: Record<string, any>
  ): React.ReactNode => {
    if (data.status == EXTERNAL_SERVICE_STATUS.PENDENTE) {
      return <span className="text-bluePrimary font-semibold">{content}</span>;
    } else if (data.status == "rejected") {
      return <span className="text-redPrimary font-semibold">{content}</span>;
    } else if (data.status == EXTERNAL_SERVICE_STATUS.PRE_APROVADO) {
      return (
        <span className="text-yellowPrimary font-semibold">{content}</span>
      );
    } else if (data.status == EXTERNAL_SERVICE_STATUS.APROVADO) {
      return <span className="text-greenDark font-semibold">{content}</span>;
    } else if (data.status == EXTERNAL_SERVICE_STATUS.CANCELADO) {
      return <span className="text-redPrimary font-semibold">{content}</span>;
    }
  };

  const actions = [
    {
      label: "Editar",
      action: (data) => {
        setAction({ eventName: "edit", data: data });
      },
      disabled: (data) => {
        return data.status == "Aprovado" || companyId == "*" ? true : false;
      },
    },
    {
      label: "Pre-Aprovar",
      action: async (data) => {
        const res = await changeStatusExternalServices(data._id);
        if (!res.ok) {
          notifyError(res.message as string);
        } else {
          notifySuccess("Pré-Aprovado Serviço com sucesso");
          setAction({ eventName: "pre-aprovar", data: data });
        }
      },
      disabled: (data) => {
        return data.status != "Pendente" ||
          companyId == "*" ||
          data.status == "Cancelado"
          ? true
          : false;
      },
    },
    {
      label: "Aprovar",
      action: async (data) => {
        const res = await changeStatusExternalServices(data._id);
        if (!res.ok) {
          notifyError(res.message as string);
        } else {
          notifySuccess("Aprovado Serviço com sucesso");
          setAction({ eventName: "aprovar", data: data });
        }
      },
      disabled: (data) => {
        return data.status != "Pré-Aprovado" ||
          companyId == "*" ||
          data.status == "Cancelado"
          ? true
          : false;
      },
    },
    {
      label: "Cancelar",
      action: (data) => {
        setAction({ eventName: "cancelar", data: data });
      },
      disabled: (data) => {
        return data.status == "Aprovado" ||
          companyId == "*" ||
          data.status == "Cancelado"
          ? true
          : false;
      },
    },
  ];
  const isAction = (eventName: string) => action.eventName === eventName;

  useEffect(() => {
    async function fetchData(filters, page) {
      setIsLoading(true);
      const resp = await getExternalServices({ ...filters, page });
      setData({ itemsPerPage: data.itemsPerPage, totalItems: resp.totalItems });
      setIsLoading(false);

      if (!resp.ok) {
        notifyError((resp as { message?: string })?.message || "unknown error");
        return;
      }

      setExternalServices(resp.data);
    }
    fetchData(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, action, companyId, companyStore]);

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };
  const STATUS_LABELS = {
    [EXTERNAL_SERVICE_STATUS.APROVADO]: EXTERNAL_SERVICE_STATUS.APROVADO,
    [EXTERNAL_SERVICE_STATUS.PENDENTE]: EXTERNAL_SERVICE_STATUS.PENDENTE,
    [EXTERNAL_SERVICE_STATUS.PRE_APROVADO]:
      EXTERNAL_SERVICE_STATUS.PRE_APROVADO,
    [EXTERNAL_SERVICE_STATUS.CANCELADO]: EXTERNAL_SERVICE_STATUS.CANCELADO,
  };

  return (
    <div className="table-section">
      <TableFilters
        dashboardHeader="Serviços Externos"
        placeholder="Digite a Descrição, Solicitante, Fornecedor ou a Placa..."
        statusOptions={STATUS_LABELS}
        newItemLabel="Add. Serviços externos"
        onNewItemClick={() => setShowCreate(true)}
        onSearch={handleFilterChange}
        entity="product"
        tableData={externalServices}
      />

      <GenericTable
        headers={headers}
        data={externalServices}
        actions={actions}
        setFilters={({ orderBy }) => {
          setFilters((prev) => ({ ...prev, orderBy }));
        }}
        renders={{
          companyId: (companyId) => {
            return getCompanySigla(companyId as string) || "";
          },
          createdAt: (createdAt, purchaseOrder) => {
            const formattedDate = formatDateAndTime(createdAt);
            return formatStyle(
              <span
                className="flex items-center gap-2"
                style={{ fontSize: "15px" }}
              >
                {formattedDate || ""}
              </span>,
              purchaseOrder as IExternalServices
            ) as React.ReactElement;
          },
          supplierName: (supplierName, externalServices) => {
            return formatStyle(
              <span
                className="flex items-center gap-2"
                style={{ fontSize: "15px" }}
              >
                {String(supplierName) || ""}
              </span>,
              externalServices as IExternalServices
            ) as React.ReactElement;
          },
          requestorName: (supplierName, externalServices) => {
            return formatStyle(
              <span
                className="flex items-center gap-2"
                style={{ fontSize: "15px" }}
              >
                {String(supplierName) || ""}
              </span>,
              externalServices as IExternalServices
            ) as React.ReactElement;
          },
          licensePlate: (licensePlate, externalServices) => {
            return formatStyle(
              <span
                className="flex items-center gap-2"
                style={{ fontSize: "15px" }}
              >
                {String(licensePlate) || ""}
              </span>,
              externalServices as IExternalServices
            ) as React.ReactElement;
          },
          status: (status, purchaseOrder) => {
            return formatStyle(
              String(status),
              purchaseOrder as IExternalServices
            ) as React.ReactElement;
          },
          totalValue: (totalValue, externalServices) => {
            return formatStyle(
              <span
                className="flex items-center gap-2"
                style={{ fontSize: "15px" }}
              >
                {String(totalValue) || ""}
              </span>,
              externalServices as IExternalServices
            ) as React.ReactElement;
          },
          description: (description, externalServices) => {
            return formatStyle(
              <span
                className="flex items-center gap-2"
                style={{ fontSize: "15px" }}
              >
                {String(description) || ""}
              </span>,
              externalServices as IExternalServices
            ) as React.ReactElement;
          },
        }}
        entity="product"
      />

      {showCreate && (
        <ModalRegisterExternalServices
          onClose={() => {
            setShowCreate(false);
          }}
        />
      )}
      {isAction("cancelar") && (
        <CancelarExternalServicesModal
          onClose={() => setAction(EMPTY_ACTION)}
          id={
            typeof action.data === "object" &&
            action.data !== null &&
            "_id" in action.data
              ? (action.data as IExternalServices)._id ?? ""
              : ""
          }
        />
      )}
      {isAction("edit") && (
        <EditExternalServicesModal
          onClose={() => setAction(EMPTY_ACTION)}
          id={(action.data as IExternalServices)?._id ?? ""}
          initial={
            typeof action.data === "object" && action.data !== null
              ? {
                  _id: (action.data as IExternalServices)._id ?? "",
                  companyId: (action.data as IExternalServices).companyId ?? "",
                  licensePlate:
                    (action.data as IExternalServices).licensePlate ?? "",
                  description:
                    (action.data as IExternalServices).description ?? "",
                  totalValue:
                    (action.data as IExternalServices).totalValue ?? 0,
                  supplierName:
                    (action.data as IExternalServices).supplierName ?? "",
                }
              : {
                  _id: "",
                  companyId: "",
                  licensePlate: "",
                  description: "",
                  totalValue: 0,
                  supplierName: "",
                }
          }
        />
      )}

      {isLoading && <Loading />}

      <TableFooter
        page={page || 1}
        totalItems={data.totalItems || 0}
        itemsPerPage={10}
        goToPage={setPage}
      />
    </div>
  );
}
